/**
 * §7.8 Stripe Payment Experience — 8-node horizontal flow showing the
 * end-to-end checkout journey, plus a Stripe-inspired mock checkout
 * card on the right.
 */

import React from 'react'
import {
  MousePointerClick, Lock, CheckCircle2, FileText, BadgeCheck,
  ServerCog, MailPlus, LayoutDashboard, Sparkles, CreditCard, ShieldCheck,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { PAYMENT_STEPS } from '../data'
import { TONE } from '../_shared'

const ICONS = {
  MousePointerClick, Lock, CheckCircle2, FileText, BadgeCheck,
  ServerCog, MailPlus, LayoutDashboard,
}

export default function Payment() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Payment Process</Eyebrow>
          <SectionHeading align="center"
            title={<>Secure Stripe checkout,{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">end-to-end automated.</span></>}
            subtitle="From clicking a licence to your dashboard being ready — eight fully-automated steps. No manual provisioning, no waiting on humans."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* LEFT — Stripe-inspired checkout card */}
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-5 backdrop-blur-xl overflow-hidden">
              <div className="pointer-events-none absolute inset-0 opacity-60"
                style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(59,130,246,0.10), transparent 70%)' }} />

              {/* Stripe header */}
              <div className="relative flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-[#635BFF] text-white text-[11px] font-bold">S</span>
                  <span className="text-[12.5px] font-semibold text-zinc-200">Stripe Checkout</span>
                </div>
                <span className="inline-flex items-center gap-1 text-[10.5px] font-mono text-emerald-400">
                  <ShieldCheck className="h-3 w-3" /> secure
                </span>
              </div>

              {/* Order summary */}
              <div className="relative rounded-lg border border-white/10 bg-black/40 p-3 mb-3">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10.5px] font-mono uppercase tracking-wider text-zinc-500">Order</div>
                  <div className="text-[10.5px] font-mono text-brand-gold">Most Popular</div>
                </div>
                <div className="flex items-baseline justify-between">
                  <div className="text-[13.5px] font-semibold text-white">Professional License</div>
                  <div className="text-[20px] font-semibold text-white">$949</div>
                </div>
                <div className="text-[10.5px] text-zinc-500 mt-1">6 months · USD</div>
              </div>

              {/* Mock card fields */}
              <div className="relative flex flex-col gap-2">
                <div className="rounded-md border border-white/10 bg-white/[0.02] px-3 py-2">
                  <div className="text-[9.5px] font-mono uppercase tracking-wider text-zinc-500">Email</div>
                  <div className="text-[12.5px] text-zinc-200">operator@alphatrade.io</div>
                </div>
                <div className="rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 flex items-center gap-2">
                  <CreditCard className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                  <div className="flex-1">
                    <div className="text-[9.5px] font-mono uppercase tracking-wider text-zinc-500">Card</div>
                    <div className="text-[12.5px] text-zinc-200">**** **** **** 4242 · 12/28</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-md border border-white/10 bg-white/[0.02] px-3 py-2">
                    <div className="text-[9.5px] font-mono uppercase tracking-wider text-zinc-500">Country</div>
                    <div className="text-[12.5px] text-zinc-200">United States</div>
                  </div>
                  <div className="rounded-md border border-white/10 bg-white/[0.02] px-3 py-2">
                    <div className="text-[9.5px] font-mono uppercase tracking-wider text-zinc-500">Tax</div>
                    <div className="text-[12.5px] text-zinc-200">Auto</div>
                  </div>
                </div>

                {/* Pay button */}
                <div className="mt-2 rounded-lg bg-gradient-to-r from-brand-gold to-[#FFE17A] px-4 py-2.5 text-center text-[13px] font-semibold text-black shadow-[0_10px_30px_-10px_rgba(255,193,7,0.6)]">
                  Pay $949
                </div>
              </div>

              {/* Wallet chips */}
              <div className="relative mt-3 flex items-center gap-1.5">
                {['Card', 'GPay', 'ApplePay', 'Netbanking'].map((w) => (
                  <span key={w} className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-wider text-zinc-400">{w}</span>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT — 8-step flow */}
          <div className="lg:col-span-7">
            <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-5 md:p-6 backdrop-blur-xl overflow-hidden">
              <div className="pointer-events-none absolute inset-0 opacity-60"
                style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.10), transparent 70%)' }} />
              <div className="relative flex items-center justify-between mb-4">
                <div className="text-[11px] font-mono uppercase tracking-wider text-brand-gold">End-to-end flow</div>
                <div className="text-[10.5px] font-mono text-emerald-400">~90 seconds</div>
              </div>
              <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {PAYMENT_STEPS.map((s, i) => {
                  const tone = TONE[s.tone] || TONE.orange
                  const Icon = ICONS[s.iconKey] || Sparkles
                  return (
                    <div key={s.id} className="relative rounded-lg border border-white/10 bg-white/[0.02] px-2.5 py-2.5 overflow-hidden">
                      <div className="pointer-events-none absolute inset-0 opacity-40"
                        style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
                      <div className="relative flex items-center gap-2">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-md border"
                          style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                          <Icon strokeWidth={1.8} style={{ width: 13, height: 13 }} />
                        </span>
                        <div className="flex flex-col">
                          <span className="text-[8.5px] font-mono uppercase tracking-wider" style={{ color: tone.hex }}>step {String(i + 1).padStart(2, '0')}</span>
                          <span className="text-[11.5px] font-semibold text-white leading-tight">{s.label}</span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="relative mt-4 flex items-center gap-2 rounded-md border border-emerald-400/25 bg-emerald-400/[0.06] px-3 py-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                <p className="text-[12.5px] text-zinc-100">
                  <span className="font-semibold text-white">Zero manual steps.</span>{' '}
                  From payment confirmation to a live dashboard, every action is automated.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
